import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { LOGO } from "../constants";
import { auth } from "../firebase";
import {
  LIGHT_PAGES,
  PHOTO_HERO_PAGES,
  NAV_ITEMS,
  SECTION_SUBNAV,
  getPrimaryNavItem,
  getSectionSubnav,
  getSectionIsDark,
} from "./nav/navConfig";
import SubNavRow from "./nav/SubNavRow";
import NavPreviewPanel from "./nav/NavPreviewPanel";
import MobileNavDrawer from "./nav/MobileNavDrawer";
import ProfileMenu from "./nav/ProfileMenu";
import LiquidGlassButton from "./LiquidGlassButton";

function getDocumentTheme() {
  if (typeof document === "undefined") {
    return "dark";
  }

  return document.documentElement.getAttribute("data-theme") || "dark";
}

export default function Nav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [overDark, setOverDark] = useState(true);
  const [theme, setTheme] = useState(getDocumentTheme);
  const [authUser, setAuthUser] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSubnavTo, setActiveSubnavTo] = useState(null);
  const [openPreviewKey, setOpenPreviewKey] = useState(null);
  const menuRef = useRef(null);
  const previewTimerRef = useRef(null);

  useEffect(() => {
    let rafId = 0;

    const update = () => {
      setScrolled((prev) => {
        const enterThreshold = 12;
        const exitThreshold = 6;
        return prev ? window.scrollY > exitThreshold : window.scrollY > enterThreshold;
      });
      setTheme(getDocumentTheme());
      setOverDark(getSectionIsDark());
    };

    const onScroll = () => {
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    const timer = setTimeout(update, 80);

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [location.pathname]);

  useEffect(() => onAuthStateChanged(auth, (user) => {
    setAuthUser(user || null);
    if (user) {
      user.getIdTokenResult().then(r => setIsAdmin(r.claims.admin === true)).catch(() => setIsAdmin(false));
    } else {
      setIsAdmin(false);
    }
  }), []);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const onEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    const onOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", onEscape);
    document.addEventListener("mousedown", onOutsideClick);

    return () => {
      document.removeEventListener("keydown", onEscape);
      document.removeEventListener("mousedown", onOutsideClick);
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setOpenPreviewKey(null);
    if (previewTimerRef.current) {
      window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!openPreviewKey) {
      return undefined;
    }

    const onEscape = (event) => {
      if (event.key === "Escape") {
        setOpenPreviewKey(null);
      }
    };

    document.addEventListener("keydown", onEscape);

    return () => {
      document.removeEventListener("keydown", onEscape);
    };
  }, [openPreviewKey]);

  const activeSection = getPrimaryNavItem(location.pathname);
  const subNavSection = getSectionSubnav(location.pathname);

  useEffect(() => {
    if (!subNavSection?.items?.length) {
      setActiveSubnavTo(null);
      return undefined;
    }

    const anchors = subNavSection.items
      .filter((item) => item.to.includes("#"))
      .map((item) => ({
        to: item.to,
        id: item.to.split("#")[1],
      }))
      .filter((item) => item.id);

    if (anchors.length === 0) {
      setActiveSubnavTo(null);
      return undefined;
    }

    const updateActive = () => {
      if (location.hash) {
        const hashMatch = anchors.find((item) => `#${item.id}` === location.hash);
        if (hashMatch) {
          setActiveSubnavTo(hashMatch.to);
          return;
        }
      }

      const navOffset = 118;
      let current = null;

      for (const anchor of anchors) {
        const element = document.getElementById(anchor.id);
        if (!element) {
          continue;
        }

        const rect = element.getBoundingClientRect();
        if (rect.top <= navOffset + 12) {
          current = anchor.to;
        }

        if (rect.top <= navOffset && rect.bottom >= navOffset) {
          current = anchor.to;
          break;
        }
      }

      setActiveSubnavTo(current);
    };

    updateActive();

    let rafId = 0;
    const onScrollOrResize = () => {
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateActive();
      });
    };

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [location.hash, location.pathname, subNavSection]);

  const handleSignOut = async () => {
    await signOut(auth);
    setMenuOpen(false);
    navigate("/login");
  };

  const clearPreviewTimer = () => {
    if (previewTimerRef.current) {
      window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  };

  const showPreview = (key) => {
    if (!key) {
      return;
    }
    clearPreviewTimer();
    setOpenPreviewKey(key);
  };

  const hidePreview = () => {
    clearPreviewTimer();
    previewTimerRef.current = window.setTimeout(() => {
      setOpenPreviewKey(null);
    }, 120);
  };

  const handlePreviewBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    hidePreview();
  };

  const dark = scrolled || LIGHT_PAGES.has(location.pathname);
  const overPhoto = !scrolled && PHOTO_HERO_PAGES.has(location.pathname);
  const overHeroDark = location.pathname === "/";
  const popupDark = theme !== "light" || dark || overPhoto || overHeroDark || overDark;
  const isInSection = (item) => activeSection?.label === item.label;

  return (
    <nav className={`nav${dark ? " scrolled" : ""}${overPhoto ? " over-photo" : ""}${overHeroDark ? " over-hero-dark" : ""}${overDark ? " over-dark" : " over-light"}${popupDark ? " nav-popup-dark" : " nav-popup-light"}`}>
      <div className="nav-inner">
        <Link className="nav-logo" to="/">
          <img src={LOGO} alt="Ateno" />
          <span>Ateno</span>
        </Link>

        <ul className="nav-links">
          {NAV_ITEMS.map((item) => {
            const previewSection = item.subnavKey ? SECTION_SUBNAV[item.subnavKey] : null;
            const previewOpen = openPreviewKey === item.subnavKey;

            return (
              <li
                key={item.label}
                className={`nav-link-shell${previewSection ? " has-preview" : ""}`}
                onMouseEnter={() => showPreview(item.subnavKey)}
                onMouseLeave={hidePreview}
                onFocus={() => showPreview(item.subnavKey)}
                onBlur={handlePreviewBlur}
              >
                <Link
                  className={`nav-link-item${isInSection(item) ? " active" : ""}`}
                  to={item.path}
                  aria-haspopup={previewSection ? "menu" : undefined}
                  aria-expanded={previewSection ? previewOpen : undefined}
                  onClick={() => setOpenPreviewKey(null)}
                >
                  {item.label}
                </Link>

                {previewSection ? (
                  <NavPreviewPanel
                    section={previewSection}
                    pathname={location.pathname}
                    hash={location.hash}
                    activeTo={activeSubnavTo}
                    popupDark={popupDark}
                    open={previewOpen}
                    onMouseEnter={() => showPreview(item.subnavKey)}
                    onMouseLeave={hidePreview}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {authUser === undefined ? null : authUser ? (
              <ProfileMenu
                authUser={authUser}
                isAdmin={isAdmin}
                menuOpen={menuOpen}
                onToggle={() => setMenuOpen((open) => !open)}
                onSignOut={handleSignOut}
                overDark={popupDark}
                menuRef={menuRef}
              />
          ) : (
            location.pathname === "/" ? (
              <LiquidGlassButton
                to="/#contact"
                width={126}
                height={36}
                tone="dark"
                className="nav-liquid-access"
                fallbackClassName="btn btn-sm btn-white"
              >
                Contact Team
              </LiquidGlassButton>
            ) : (
              <Link to="/#contact" className="btn btn-sm btn-white" style={{ fontSize: 13 }}>
                Contact Team
              </Link>
            )
          )}

          <button className="nav-hamburger" onClick={() => setMobileOpen((open) => !open)} aria-label="Menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {mobileOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="4" y1="7" x2="20" y2="7" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="17" x2="20" y2="17" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      <SubNavRow
        section={subNavSection}
        pathname={location.pathname}
        hash={location.hash}
        activeTo={activeSubnavTo}
      />

      <MobileNavDrawer
        mobileOpen={mobileOpen}
        navItems={NAV_ITEMS}
        pathname={location.pathname}
        hash={location.hash}
        section={activeSection}
        subNavSection={subNavSection}
        activeTo={activeSubnavTo}
        authUser={authUser}
        onClose={() => setMobileOpen(false)}
        onSignOut={() => {
          handleSignOut();
          setMobileOpen(false);
        }}
      />
    </nav>
  );
}
